import { useState, useEffect } from "react";
import FacilityList from "./components/FacilityList";
import PrefectureSelect from "./components/PrefectureSelect";
import KeywordInput from "./components/KeywordInput";
import OptionSelect from "./components/OptionSelect";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookmark, faXmark } from "@fortawesome/free-solid-svg-icons";
import styles from "./App.module.css";

// todo: 医療機関のデータが更新されて増減があるとブックマークがずれるので、ハッシュ値を使ってデータの整合性を確認する

// データをlocalStorageに保存する関数
const saveToLocalStorage = (key, value) => {
  try {
    const serializedValue = JSON.stringify(value);
    localStorage.setItem(key, serializedValue);
  } catch (err) {
    console.error("Error saving to localStorage", err);
  }
};

// localStorageからデータを読み込む関数
const loadFromLocalStorage = (key) => {
  try {
    const serializedValue = localStorage.getItem(key);
    if (serializedValue === null) {
      return undefined;
    }
    return JSON.parse(serializedValue);
  } catch (err) {
    console.error("Error loading from localStorage", err);
    return undefined;
  }
};

// 都道府県ごとに市区町村を整理する関数
const organizeDataByPrefecture = (data) => {
  const result = {};

  data.forEach(item => {
    // 都道府県がすでにresultオブジェクトに存在するかチェック
    if (!result[item.pref]) {
      result[item.pref] = {};
    }
    // 市区町村がその都道府県のオブジェクトに存在するかチェック
    if (!result[item.pref][item.city]) {
      result[item.pref][item.city] = [];
    }
    // 市区町村の配列に病院の名前を追加
    result[item.pref][item.city].push(item.name);
  });

  // 結果を都道府県ごとの市区町村の配列を持つオブジェクトの配列に変換
  return Object.keys(result).map(pref => ({
    pref: pref,
    cities: Object.keys(result[pref]).map(city => ({
      city: city
    }))
  }));
}

function App() {
  const { t } = useTranslation();
  const [prefecture, setPrefecture] = useState("");
  const [city, setCity] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [activePrefecture, setActivePrefecture] = useState([]);
  const [dataHash, setDataHash] = useState(
    () => loadFromLocalStorage("dataHash") || ""
  );
  const [bookmarkedFacilities, setBookmarkedFacilities] = useState(
    () => loadFromLocalStorage("bookmarkedFacilities") || []
  );
  const [showBookmark, setShowBookmark] = useState(false);
  const [filter, setFilter] = useState({
    keyword: "",
    options: { course: "", sikyu: false, manmo: false, echo: false},
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const dataUrl = process.env.REACT_APP_DATA_URL;
        const response = await fetch(dataUrl);
        const data = await response.json();

        setFacilities(data);
        const prefs = organizeDataByPrefecture(data);
        setActivePrefecture(prefs);

        // calc hash
        const newHash = await calculateSHA256Hash(data);

        const oldHash = loadFromLocalStorage("dataHash");

        if (newHash !== oldHash) {
          setBookmarkedFacilities([]);
          saveToLocalStorage("bookmarkedFacilities", []); // リセットしたブックマーク情報をlocalStorageに保存
        }

        setDataHash(newHash);
      } catch (error) {
        console.log(error);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    saveToLocalStorage("dataHash", dataHash);
  }, [dataHash]);

  useEffect(() => {
    saveToLocalStorage("bookmarkedFacilities", bookmarkedFacilities);
    if (bookmarkedFacilities.length === 0) {
      setShowBookmark(false);
    }
  }, [bookmarkedFacilities]);

  const handleFilterChange = (key, value) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
  };

  const handlePrefectureSelect = (value) => {
    setPrefecture(value);
  };

  const handleCitySelect = (value) => {
    setCity(value);
  }

  const handleBookmark = (facilityId, flag) => {
    setBookmarkedFacilities((prev) => {
      if (flag) {
        return [...prev, facilityId];
      } else {
        return prev.filter((id) => id !== facilityId);
      }
    });
  };

  const handleShowBookmark = () => {
    setShowBookmark(!showBookmark);
  };

  async function calculateSHA256Hash(jsonData) {
    // JSONデータを文字列に変換
    const jsonString = JSON.stringify(jsonData);

    // 文字列をUint8Array（バイト配列）に変換
    const encoder = new TextEncoder();
    const data = encoder.encode(jsonString);

    // SHA-256ハッシュを計算
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);

    // ハッシュ値を16進数の文字列に変換
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    return hashHex;
  }

  const filteredFacilities = facilities.filter((facility) => {
    if (bookmarkedFacilities.length > 0 && showBookmark) {
      return bookmarkedFacilities.includes(facility.id);
    } else if (filter.area === false){
      return [];
    } else {
      return (
        (prefecture.length === 0 || prefecture.includes(facility.pref)) &&
        (city.length === 0 || city.includes(facility.city)) &&
        (filter.keyword === "" ||
          facility.address.includes(filter.keyword) ||
          facility.name.includes(filter.keyword)) &&
        (filter.options.course === "" ||
          facility[filter.options.course] !== "") &&
        (filter.options.sikyu === false || facility.sikyu !== "") &&
        (filter.options.manmo === false || facility.manmo !== "") &&
        (filter.options.echo === false || facility.echo !== "")
      );
    }
  });

  return (
    <div className={styles.container}>
      <PrefectureSelect
        onPrefectureSelect={handlePrefectureSelect}
        onCitySelect={handleCitySelect}
        activePrefecture={activePrefecture}
        activeCity={city}
      />
      <OptionSelect
        onChange={(value) => handleFilterChange("options", value)}
      />
      <div className={styles.resultHeader}>
        <div
          style={{ display: filteredFacilities.length > 0 ? "block" : "none" }}
        >
          {t("{{count}}件の施設があります。", {
            count: filteredFacilities.length,
          })}
        </div>
        <div
          style={{
            display: filteredFacilities.length === 0 ? "block" : "none",
          }}
        >
          {t("条件に該当する施設が見つかりませんでした。")}
        </div>
        <KeywordInput
          onChange={(value) => handleFilterChange("keyword", value)}
        />
        {bookmarkedFacilities.length > 0 && (
          <div className={styles.showBookmarkButton}>
            <button onClick={handleShowBookmark}>
              <span className={styles.faIcon}>
                <FontAwesomeIcon icon={showBookmark ? faXmark : faBookmark} />
              </span>
              {t("ブックマークを表示")}({bookmarkedFacilities.length})
            </button>
          </div>
        )}
      </div>
      <FacilityList
        filteredFacilities={filteredFacilities}
        handleBookmark={handleBookmark}
        bookmarkedFacilities={bookmarkedFacilities}
      />
    </div>
  );
}

export default App;
